import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
} from '@/components/ui'
import { useExceptionStats, useSubmissionList } from '../api/queries'
import { SubmissionDetailDrawer } from '../components/SubmissionDetailDrawer'
import type { SubmissionSummary } from '../api/types'

function ageLabel(submittedAt: string | null): string {
  if (!submittedAt) return '—'
  const hours = (Date.now() - new Date(submittedAt).getTime()) / 3_600_000
  if (hours < 24) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}

function ageClassName(submittedAt: string | null): string {
  if (!submittedAt) return ''
  const days = (Date.now() - new Date(submittedAt).getTime()) / 86_400_000
  if (days >= 3) return 'text-danger font-medium'
  if (days >= 1) return 'text-warning'
  return ''
}

function statusVariant(status: string): 'warning' | 'danger' {
  return status === 'pending_review' ? 'warning' : 'danger'
}

function statusLabel(status: string) {
  if (status === 'pending_review') return 'Pending Review'
  if (status === 'validation_failed') return 'Validation Failed'
  return 'Post Failed'
}

function failureSummary(s: SubmissionSummary): string {
  const rules = new Map<string, string>()
  for (const ln of s.lines ?? []) {
    for (const r of ln.validation_results ?? []) {
      if (!r.passed) rules.set(r.rule_code, r.label ? `${r.rule_code} · ${r.label}` : r.rule_code)
    }
  }
  return rules.size > 0 ? Array.from(rules.values()).join(', ') : s.last_error ?? '—'
}

const columns: ColumnDef<SubmissionSummary>[] = [
  {
    id: 'shipment_ref',
    header: 'Shipment Ref',
    accessorKey: 'shipment_ref',
    cell: (ctx) => <span className="text-role-data-primary">{ctx.getValue() as string}</span>,
  },
  {
    id: 'po_ref',
    header: 'PO Ref',
    accessorKey: 'po_ref',
    cell: (ctx) => <span className="text-role-data-secondary">{ctx.getValue() as string}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: (ctx) => (
      <Chip variant={statusVariant(ctx.getValue() as string)} size="xs">
        {statusLabel(ctx.getValue() as string)}
      </Chip>
    ),
  },
  {
    id: 'failure',
    header: 'Failure',
    accessorFn: (row) => failureSummary(row),
    cell: (ctx) => (
      <span className="text-role-table-cell text-danger">{ctx.getValue() as string}</span>
    ),
  },
  {
    id: 'submitted_at',
    header: 'Submitted',
    accessorKey: 'submitted_at',
    cell: (ctx) => {
      const v = ctx.getValue() as string | null
      if (!v) return <span className="text-role-metadata">—</span>
      return (
        <span className="text-role-metadata">
          {new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      )
    },
  },
  {
    id: 'age',
    header: 'Age',
    accessorFn: (row) => row.submitted_at,
    cell: (ctx) => {
      const v = ctx.row.original.submitted_at
      return (
        <span className={`text-role-table-cell ${ageClassName(v)}`}>
          {ageLabel(v)}
        </span>
      )
    },
  },
]

export function UvivaExceptionQueuePage() {
  const [selected, setSelected] = useState<SubmissionSummary | null>(null)

  const { data: stats } = useExceptionStats()
  const { data: pendingData, isLoading: loadingPending } = useSubmissionList({
    status: 'pending_review',
    limit: 50,
  })
  const { data: failedData, isLoading: loadingFailed } = useSubmissionList({
    status: 'post_failed',
    limit: 50,
  })
  const { data: validationFailedData, isLoading: loadingValidationFailed } = useSubmissionList({
    status: 'validation_failed',
    limit: 50,
  })

  const rows = useMemo(() => [
    ...(pendingData?.items ?? []),
    ...(failedData?.items ?? []),
    ...(validationFailedData?.items ?? []),
  ].sort((a, b) => {
    const da = a.submitted_at ? new Date(a.submitted_at).getTime() : 0
    const db = b.submitted_at ? new Date(b.submitted_at).getTime() : 0
    return da - db // oldest first
  }), [pendingData, failedData, validationFailedData])

  const isLoading = loadingPending || loadingFailed || loadingValidationFailed
  const totalPending = (stats?.pending_review ?? 0) + (stats?.post_failed ?? 0) + (stats?.validation_failed ?? 0)

  return (
    <>
      <div className="flex flex-col h-full bg-canvas">
        <header className="flex items-center justify-between gap-6 border-b border-border-subtle bg-surface px-6 py-3">
          <div className="flex items-center gap-3">
            <h1 className="text-role-page-title">Exception Queue</h1>
            {totalPending > 0 && (
              <Chip variant="danger" size="xs">{totalPending} pending</Chip>
            )}
          </div>
          {stats && (
            <MetricStrip variant="inline">
              <MetricCard
                variant="inline"
                label="Pending Review"
                value={stats.pending_review}
                valueTone={stats.pending_review > 0 ? 'warning' : 'default'}
              />
              <MetricCard
                variant="inline"
                label="Post Failed"
                value={stats.post_failed}
                valueTone={stats.post_failed > 0 ? 'danger' : 'default'}
              />
              <MetricCard
                variant="inline"
                label="Validation Failed"
                value={stats.validation_failed}
                valueTone={stats.validation_failed > 0 ? 'danger' : 'default'}
              />
              <MetricCard variant="inline" label="Posted (all time)" value={stats.posted} />
            </MetricStrip>
          )}
        </header>

        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="text-role-metadata py-8 text-center">Loading exceptions…</div>
          ) : rows.length === 0 ? (
            <EmptyState
              variant="no-data"
              title="Queue is clear"
              description="No submissions need review or retry."
            />
          ) : (
            <DataTable
              moduleId="uviva-exception-queue"
              columns={columns}
              data={rows}
              onRowClick={(row) => setSelected(row)}
              criticalRowPredicate={() => true}
            />
          )}
        </div>
      </div>

      <SubmissionDetailDrawer
        submission={selected}
        onClose={() => setSelected(null)}
        canApprove
      />
    </>
  )
}
